// Dates are typed as strings because they arrive as ISO strings in the JSON response.
export const PHARMACY_DETAIL_INCLUDE_PAYMENT_INFO = 'paymentInfo';
export const PHARMACY_DETAIL_INCLUDE_ENROLLMENTS = 'enrollments';

export type PharmacyDetailIncludeType = typeof PHARMACY_DETAIL_INCLUDE_PAYMENT_INFO | typeof PHARMACY_DETAIL_INCLUDE_ENROLLMENTS;

export interface PharmacyDetailOptionsModel {
  include?: PharmacyDetailIncludeType[];
  tenantIds?: string[];
}

export interface PharmacyAddressModel {
  lineOne?: string;
  lineTwo?: string;
  city?: string;
  county?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lon?: number;
}

export interface PharmacyMailingAddressModel {
  lineOne?: string;
  lineTwo?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface PharmacyContactModel {
  email?: string;
  phoneNumber?: string;
  mobilePhoneNumber?: string;
  faxNumber?: string;
}

export interface PharmacyContactPersonModel {
  title?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

export interface PharmacyPersonNameModel {
  firstName?: string;
  lastName?: string;
}

export interface PharmacyPbaEnrollmentModel {
  systemVendor?: string;
  isPaymentAdjudicationEnabled?: boolean;
}

export interface PharmacyModel {
  ncpdp: string;
  npi?: string;
  name: string;
  address?: PharmacyAddressModel;
  mailingAddress?: PharmacyMailingAddressModel;
  contact?: PharmacyContactModel;
  contactPerson?: PharmacyContactPersonModel;
  owner?: PharmacyPersonNameModel;
  pic?: PharmacyPersonNameModel;
  parentOrganization?: { name?: string };
  pbaEnrollment?: PharmacyPbaEnrollmentModel;
  joinedAt?: string;
  taxId?: string;
  fedTaxId?: string;
  stateTaxId?: string;
  msa?: string;
  dbaName?: string;
  legalName?: string;
  family?: string;
  chainNpi?: string;
  deaRegistrationId?: string;
  deaExpirationId?: string;
  achRemittanceEmail?: string;
  affiliationCode?: string;
  is835FileRequired?: boolean;
  isRelayhealthParticipant?: boolean;
  softwareInstructionsSentAt?: string;
}

export interface PharmacyPaymentInfoModel {
  pharmacyNpi: string;
  bankAccountNumber?: string;
  bankRoutingNumber?: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  destinationId?: string;
  remittanceAdviceStartDate?: string;
  authorizationAttachments?: string[];
  bankLetterFiles?: string[];
  isPaymentEnabled?: boolean;
  is835Enabled?: boolean;
  note?: string;
}

export interface PharmacyEnrollmentGroupModel {
  id: string;
  name: string;
  isBlocked: boolean;
  isPermanentlyBlocked: boolean;
  isBlockedAsOldNpi: boolean;
}

export interface PharmacyEnrollmentEngagementsModel {
  pba?: {
    isApprovedResponseEnabled?: boolean;
    responseSettings?: {
      paidCapturedResponseOccList?: string[];
      matchingCapturedResponseOccList?: string[];
    };
  };
  brightscrip?: {
    isScorecardEnabled?: boolean;
    isPharmacyPatientEngagementEnabled?: boolean;
  };
  rxLogistics?: {
    isParticipant?: boolean;
  };
}

export interface PharmacyEnrollmentModel {
  tenant: { id: string; name?: string };
  enrollmentGroupId?: string;
  enrollmentGroup?: PharmacyEnrollmentGroupModel;
  engagements?: PharmacyEnrollmentEngagementsModel;
  isConsignmentHub?: boolean;
  isVisibleToRam?: boolean;
  fieldAdmin?: string;
  preferredTransferMethod?: string;
  businessRegionId?: string;
}

export interface PharmacyDetailResponseModel {
  pharmacy: PharmacyModel;
  pharmacyChain?: { npi: string; name?: string };
  systemVendor?: { id: string; name?: string };
  paymentInfo?: PharmacyPaymentInfoModel;
  enrollments?: PharmacyEnrollmentModel[];
}

export type PharmacyDetailModel = PharmacyModel & Omit<PharmacyDetailResponseModel, 'pharmacy'>;

export interface PharmacyPaymentInfoRequestModel {
  bankAccountNumber?: string | null;
  bankRoutingNumber?: string | null;
  contactEmail?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  destinationId?: string | null;
  remittanceAdviceStartDate?: string | null;
  authorizationAttachments?: string[] | null;
  bankLetterFiles?: string[] | null;
  isPaymentEnabled?: boolean | null;
  is835Enabled?: boolean | null;
  note?: string | null;
}

export interface PharmacyRequestModel {
  ncpdp?: string;
  npi?: string | null;
  name?: string | null;
  chainNpi?: string | null;
  family?: string | null;
  joinedAt?: string | null;
  taxId?: string | null;
  fedTaxId?: string | null;
  stateTaxId?: string | null;
  msa?: string | null;
  dbaName?: string | null;
  legalName?: string | null;
  deaRegistrationId?: string | null;
  deaExpirationId?: string | null;
  achRemittanceEmail?: string | null;
  affiliationCode?: string | null;
  is835FileRequired?: boolean | null;
  isRelayhealthParticipant?: boolean | null;
  softwareInstructionsSentAt?: string | null;
  parentOrganizationName?: string | null;
  addressLineOne?: string | null;
  addressLineTwo?: string | null;
  addressCity?: string | null;
  addressCounty?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  addressLat?: number | null;
  addressLon?: number | null;
  mailingAddressLineOne?: string | null;
  mailingAddressLineTwo?: string | null;
  mailingAddressCity?: string | null;
  mailingAddressState?: string | null;
  mailingAddressZip?: string | null;
  contactEmail?: string | null;
  contactPhoneNumber?: string | null;
  contactMobilePhoneNumber?: string | null;
  contactFaxNumber?: string | null;
  contactPersonTitle?: string | null;
  contactPersonFirstName?: string | null;
  contactPersonLastName?: string | null;
  contactPersonPhone?: string | null;
  contactPersonEmail?: string | null;
  ownerFirstName?: string | null;
  ownerLastName?: string | null;
  picFirstName?: string | null;
  picLastName?: string | null;
  pbaEnrollmentSystemVendor?: string | null;
  pbaEnrollmentIsPaymentAdjudicationEnabled?: boolean | null;
  changeNote?: string | null;
  paymentInfo?: PharmacyPaymentInfoRequestModel;
}

export interface PharmacySaveResponseModel {
  pharmacy: PharmacyModel;
  paymentInfo?: PharmacyPaymentInfoModel;
}

export interface PharmacyOnboardingInstructionsRequestModel {
  subject: string;
  message: string;
}

export interface PharmacyEnrollmentUpdateRequestModel {
  enrollmentGroupId?: string | null;
  isConsignmentHub?: boolean | null;
  isVisibleToRam?: boolean | null;
  fieldAdmin?: string | null;
  preferredTransferMethod?: string | null;
  engagementsRxLogisticsIsParticipant?: boolean | null;
  engagementsPbaIsApprovedResponseEnabled?: boolean | null;
  engagementsPbaResponseSettingsPaidCapturedResponseOccList?: string[] | null;
  engagementsPbaResponseSettingsMatchingCapturedResponseOccList?: string[] | null;
  engagementsBrightscripIsScorecardEnabled?: boolean | null;
  engagementsBrightscripIsPharmacyPatientEngagementEnabled?: boolean | null;
  changeNote?: string | null;
}
